/**
 * PATH: apps/qa/src/pages/report-editor/ReportDocumentEditor.tsx
 * FILE_ID: QA-REPORT-EDITOR-ADAPTER-001
 * PURPOSE: Sole QA adapter for the shared versioned document editor.
 * WHY: QA currently persists section strings while the package is JSON-first.
 */

import { useMemo } from 'react'
import {
  DocumentEditor,
  deserializeDocument,
  exportDocumentHtml,
  importDocumentHtml,
  type VersionedDocument,
} from '@quotech/document-editor'

interface ReportDocumentEditorProps {
  content: string
  title: string
  readOnly?: boolean
  onChange: (content: string) => void
  onOpenExactPdfPreview: () => void
}

export function ReportDocumentEditor({ content, title, readOnly, onChange, onOpenExactPdfPreview }: ReportDocumentEditorProps) {
  const document = useMemo(() => parsePersistedContent(content, title), [content, title])
  return (
    <DocumentEditor
      document={document}
      readOnly={readOnly}
      ariaLabel={`${title} report section`}
      onOpenExactPdfPreview={onOpenExactPdfPreview}
      onChange={({ document: nextDocument }) => onChange(exportDocumentHtml(nextDocument))}
    />
  )
}

function parsePersistedContent(content: string, title: string): VersionedDocument {
  if (content.trim().startsWith('{')) {
    try {
      return deserializeDocument(content)
    } catch {
      // Legacy or malformed JSON remains visible as source text.
    }
  }
  return importDocumentHtml(legacyTextToHtml(content), { metadata: { title, domain: 'qa-report' } })
}

function legacyTextToHtml(value: string) {
  const escaped = value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return escaped.split(/\n{2,}/).map((block) => `<p>${block.replace(/\n/g, '<br>')}</p>`).join('')
}
